import { diagram } from '@diagram/state/diagram';
import { Helpers, SideEffect } from '@diagram/state/effects/diagram/helpers';

import {
    DiagramConnection,
    getDefaultTemperature,
    getReferenceInstallationMethod,
    isUnderground,
    NECInstallationMethod,
} from 'models';

type InstallationMethod =
    | DiagramConnection['installation']['method']
    | DiagramConnection['installation']['referenceMethod'];

const syncConnectionInstallationMethod: SideEffect = (change) => {
    const { type, data, previousData } = change;

    if (type === 'delete') return;

    for (const [path, value] of Object.entries(data)) {
        const match = Helpers.matchConnection<{
            field: 'method' | 'referenceMethod';
        }>(path, /installation\.(?<field>method|referenceMethod)$/);

        if (!match) {
            continue;
        }

        const { id, field } = match;
        const connection = diagram.connections[id];

        if (!connection) {
            continue;
        }

        const method = value as InstallationMethod;
        const previousMethod = ((previousData ?? {}) as Record<string, unknown>)[path] as InstallationMethod;

        if (field === 'method') {
            syncReferenceInstallationMethod(connection, method as DiagramConnection['installation']['method']);
        }

        applyInstallationEnvironmentDefaults(connection, method, previousMethod);
    }
};

// NEC methods are used on the connection directly; only IEC installation methods map to a
// reference method (and related cable properties), which is what the sizing tables use.
const syncReferenceInstallationMethod = (
    connection: DiagramConnection,
    method: DiagramConnection['installation']['method'],
) => {
    if (!method || method === NECInstallationMethod.FREE_AIR || method === NECInstallationMethod.BURIED) {
        return;
    }

    if (!connection.cores) {
        return;
    }

    try {
        const { referenceMethod, cableOrientation, cableSupportType, cores } = getReferenceInstallationMethod(
            method,
            connection.cores,
        );

        const previousReferenceMethod = connection.installation.referenceMethod;

        connection.installation.referenceMethod = referenceMethod;

        if (cableOrientation) {
            connection.installation.cableOrientation = cableOrientation;
        }

        if (cableSupportType) {
            connection.installation.cableSupportType = cableSupportType;
        }

        if (cores) {
            connection.cores = cores;
        }

        applyInstallationEnvironmentDefaults(connection, referenceMethod, previousReferenceMethod);
    } catch (e) {
        console.error('Failed to sync installation method', e);
    }
};

// When a connection moves between above-ground and underground, reset the ambient
// temperature to the default for the new environment. Underground cables are always bunched.
const applyInstallationEnvironmentDefaults = (
    connection: DiagramConnection,
    method: InstallationMethod,
    previousMethod: InstallationMethod | undefined,
) => {
    if (!method || method === previousMethod) {
        return;
    }

    const isUndergroundMethod = isUnderground(method);
    const wasUndergroundMethod = previousMethod ? isUnderground(previousMethod) : false;

    if (previousMethod && isUndergroundMethod === wasUndergroundMethod) {
        return;
    }

    if (connection.installation?.temperature?.value) {
        connection.installation.temperature.value = getDefaultTemperature(isUndergroundMethod);
    }

    if (isUndergroundMethod && !wasUndergroundMethod && connection.installation) {
        connection.installation.cableArrangement = 'bunched';
    }
};

export { syncConnectionInstallationMethod };
